import { Helmet } from 'react-helmet-async';
import {
  Container,
  Table,
  TableHead,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  ThemeProvider,
  TablePagination,
  Box,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Grid,
} from '@mui/material';
import { useState, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

import { POPUP_DURATION_SUCCESS, usePopup } from '../../../context/PopupContext';
import { LoadingUI } from '../../../utils/LayoutStyles';
import { HttpErrorHandler, ErrorUI } from '../../../utils/ErrorHandler';
import { CustomTextField } from '../../../utils/StyledInputs';

import {
  DefaultContainedButton,
  DefaultOutlinedButton,
  CustomIconButton,
  SearchIconButton,
} from '../../../utils/Buttons';
import { PaginationAPI, theme } from '../../../utils/TableStyles';
import API from '../../../api';

//----------------------------------------------------------------------

export default function CitiesFiscalCode() {
  const [citiesResponseData, setCitiesResponseData] = useState({});
  const [citiesList, setCitiesList] = useState([]);
  const [citiesListCalled, setCitiesListCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    getCitiesList(page + 1);
  }, []);

  const getCitiesList = (page) => {
    API.get(`/citiesfcinfo?page=${page}`)
      .then((response) => {
        setCitiesResponseData(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  useEffect(() => {
    if (Object.keys(citiesResponseData).length > 0) {
      setCitiesList(citiesResponseData.data);
    }
  }, [citiesResponseData]);

  useEffect(() => {
    if (citiesList.length > 0) {
      setCitiesListCalled(true);
    }
  }, [citiesList]);

  const [errors, setErrors] = useState({
    name: false,
    registry_code: false,
    province_abbr: false,
  });

  /* ---------------------------------- Edit ---------------------------------- */
  const [cityToEdit, setCityToEdit] = useState({});
  const [openEditCity, setOpenEditCity] = useState(false);

  const handleOpenEditCity = (city) => {
    setCityToEdit(city);
    setOpenEditCity(true);
  };

  const handleCloseEditCity = () => {
    setCityToEdit({});
    setOpenEditCity(false);

    setErrors((prevErrors) =>
      Object.keys(prevErrors).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    );
  };

  const handleEditCity = (e) => {
    e.preventDefault();

    let itemsLeft = false;
    const newErrors = { ...errors };

    Object.keys(cityToEdit).forEach((key) => {
      if (key !== 'province_abbr') {
        const value = cityToEdit[key];

        if (Object.prototype.hasOwnProperty.call(cityToEdit, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0) ||
            (Array.isArray(value) && value.length === 0)
          ) {
            itemsLeft = true;
            if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
              newErrors[key] = true;
            }
            showPopup('warning', 'Inserisci i dati mancanti');
          } else {
            newErrors[key] = false;
          }
        }
      }
    });

    setErrors(newErrors);

    if (!itemsLeft) {
      API.put(`/citiesfcinfo/${cityToEdit.id}`, cityToEdit)
        .then((response) => {
          showPopup(true, response.data.message);
          getCitiesList(page + 1);
          setTimeout(() => {
            handleCloseEditCity();
          }, POPUP_DURATION_SUCCESS);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  /* ---------------------------------- Store --------------------------------- */
  const [newCity, setNewCity] = useState({
    name: '',
    registry_code: '',
    province_abbr: '',
  });

  const [openCreateCity, setOpenCreateCity] = useState(false);

  const handleOpenCreateCity = () => {
    setOpenCreateCity(true);
  };

  const handleCloseCreateCity = () => {
    setOpenCreateCity(false);

    setNewCity({
      name: '',
      registry_code: '',
      province_abbr: '',
    });

    setErrors((prevErrors) =>
      Object.keys(prevErrors).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    );
  };

  const handleCreateNewCity = (e) => {
    e.preventDefault();

    let itemsLeft = false;
    const newErrors = { ...errors };

    Object.keys(newCity).forEach((key) => {
      if (key !== 'province_abbr') {
        const value = newCity[key];

        if (Object.prototype.hasOwnProperty.call(newCity, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0) ||
            (Array.isArray(value) && value.length === 0)
          ) {
            itemsLeft = true;
            if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
              newErrors[key] = true;
            }
            showPopup('warning', 'Inserisci i dati mancanti');
          } else {
            newErrors[key] = false;
          }
        }
      }
    });

    setErrors(newErrors);

    if (!itemsLeft) {
      API.post('/citiesfcinfo', newCity)
        .then((response) => {
          showPopup(true, response.data.message);
          getCitiesList(page + 1);
          setTimeout(() => {
            handleCloseCreateCity();
          }, POPUP_DURATION_SUCCESS);
        })
        .catch((error) => {
          console.log(error);
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  /* --------------------------------- Search --------------------------------- */
  const [searchedCity, setSearchedCity] = useState('');
  const [blockSearchRefresh, setBlockSearchRefresh] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();

    if (searchedCity !== '') {
      API.get(`/citiesfcinfo?search=${searchedCity}`)
        .then((response) => {
          setCitiesResponseData(response.data);
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });

      setBlockSearchRefresh(false);
    } else {
      if (!blockSearchRefresh) {
        getCitiesList(page + 1);
      }
      setBlockSearchRefresh(true);
    }
  };

  /* ------------------------------- Pagination ------------------------------- */
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;
  const sliceStartDisplay = 0;
  const sliceEndDisplay = 50;

  // empty rows number
  const emptyRows = page > 0 ? Math.max(0, 1 * rowsPerPage - citiesList.length) : 0;

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /* ---------------------------------- Popup --------------------------------- */
  const { showPopup, setPopUpText } = usePopup();

  return (
    <>
      <Helmet>
        <title>Anagrafiche Comuni</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ paddingBottom: '24px' }}>
          <DefaultContainedButton
            startIcon={<AddIcon />}
            onClick={handleOpenCreateCity}
            disabled={errorMessage.length > 0}
          >
            Aggiungi Comune
          </DefaultContainedButton>
        </Stack>

        {errorMessage.length === 0 ? (
          <>
            <form onSubmit={(e) => handleSearch(e)}>
              <Stack
                direction="row"
                alignItems="center"
                gap="10px"
                sx={{
                  paddingBottom: ' 24px',
                }}
              >
                <CustomTextField
                  fullWidth
                  size="small"
                  type="search"
                  label="Cerca"
                  value={searchedCity}
                  onChange={(e) => {
                    setSearchedCity(e.currentTarget.value);
                  }}
                />

                <SearchIconButton onClick={(e) => handleSearch(e)}>
                  <SearchIcon />
                </SearchIconButton>
              </Stack>
            </form>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome Comune</TableCell>
                    <TableCell align="center">Codice Comune</TableCell>
                    <TableCell align="center">Provincia</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                {citiesListCalled ? (
                  <TableBody>
                    {citiesList.slice(sliceStartDisplay, sliceEndDisplay).map((city) => (
                      <TableRow key={city.registry_code}>
                        <TableCell>{city.name}</TableCell>
                        <TableCell align="center">{city.registry_code}</TableCell>
                        <TableCell align="center">{city.province_abbr}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" justifyContent="end">
                            <Box>
                              <Tooltip title="Modifica Anagrafica" arrow placement="top">
                                <Box>
                                  <CustomIconButton onClick={() => handleOpenEditCity(city)}>
                                    <EditIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            </Box>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                    {emptyRows > 0 && citiesListCalled && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={4} />
                      </TableRow>
                    )}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                        {LoadingUI()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                {citiesListCalled && (
                  <TableFooter>
                    <TableRow>
                      <ThemeProvider theme={theme}>
                        <TablePagination
                          sx={{ border: 0 }}
                          rowsPerPageOptions={[]}
                          count={citiesResponseData.total}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          ActionsComponent={(props) => (
                            <PaginationAPI
                              {...props}
                              elements={50}
                              apiResponseData={citiesResponseData}
                              setApiResponseData={setCitiesResponseData}
                              lastPage={citiesResponseData.last_page}
                            />
                          )}
                        />
                      </ThemeProvider>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          </>
        ) : (
          ErrorUI(errorMessage)
        )}
        <Dialog fullWidth open={openCreateCity} onClose={() => {}}>
          <DialogTitle>Aggiungi Comune</DialogTitle>

          <form onSubmit={handleCreateNewCity}>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Nome Comune"
                    value={newCity?.name}
                    onChange={(e) => {
                      const capitalizedValue =
                        e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1).toLowerCase();
                      setNewCity({ ...newCity, name: capitalizedValue });
                    }}
                    error={errors.name}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Codice Comune"
                    value={newCity?.registry_code}
                    onChange={(e) => setNewCity({ ...newCity, registry_code: e.target.value.toUpperCase() })}
                    error={errors.registry_code}
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Abbreviazione Provincia"
                    value={newCity?.province_abbr}
                    onChange={(e) => setNewCity({ ...newCity, province_abbr: e.target.value.toUpperCase() })}
                    error={errors.province_abbr}
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <DefaultOutlinedButton onClick={handleCloseCreateCity}>Chiudi</DefaultOutlinedButton>
              <DefaultContainedButton type="submit" customStyles={{ height: '34px' }}>
                Crea
              </DefaultContainedButton>
            </DialogActions>
          </form>
        </Dialog>
        <Dialog fullWidth open={openEditCity} onClose={() => {}}>
          <DialogTitle>Modifica Comune</DialogTitle>

          <form onSubmit={handleEditCity}>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Nome Comune"
                    value={cityToEdit?.name}
                    onChange={(e) => {
                      setCityToEdit({ ...cityToEdit, name: e.target.value });
                    }}
                    error={errors.name}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Codice Comune"
                    value={cityToEdit?.registry_code}
                    onChange={(e) => setCityToEdit({ ...cityToEdit, registry_code: e.target.value.toUpperCase() })}
                    error={errors.registry_code}
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Abbreviazione Provincia"
                    value={cityToEdit?.province_abbr}
                    onChange={(e) => setCityToEdit({ ...cityToEdit, province_abbr: e.target.value.toUpperCase() })}
                    error={errors.province_abbr}
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <DefaultOutlinedButton onClick={handleCloseEditCity}>Chiudi</DefaultOutlinedButton>
              <DefaultContainedButton type="submit" customStyles={{ height: '34px' }}>
                Modifica
              </DefaultContainedButton>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </>
  );
}
